import { isBlocklistedNumber } from "@/shared-utils";

/** A US MDN is 10 digits. The partner notes API takes it as `subscriberNumber`. */
const SUBSCRIBER_NUMBER_DIGITS = 10;

/**
 * What Amazon Connect puts in the caller endpoint when caller ID is withheld or the IVR gave up.
 * These reach us as the "phone number" and were being forwarded verbatim as a subscriber number.
 *
 * Kept in code rather than partner config on purpose: they are Amazon Connect and carrier native
 * values rather than anything partner specific, and a config load failure must not reopen the hole.
 * `Timeout` is included because it can arrive from the Channel Share DB as well as the endpoint.
 */
const SUBSCRIBER_NUMBER_SENTINELS = new Set([
    "anonymous",
    "restricted",
    "unavailable",
    "timeout",
    "unknown",
    "private",
]);

export type SummaryDispatchRejection =
    | "empty"
    | "sentinel"
    | "not-ten-digits"
    | "non-subscriber-number"
    | "blocklisted"
    | "account-number-length";

export interface SummaryDispatchVerdict {
    /** Gates the `sendToPartner` meta, which is what triggers the partner notes call. */
    canSendToPartner: boolean;
    /** Whether the dispatched identifier looks usable. Labels the `SessionSummary_Stats` event. */
    identifierIsUsable: boolean;
    reason?: SummaryDispatchRejection;
}

const ALLOWED: SummaryDispatchVerdict = Object.freeze({ canSendToPartner: true, identifierIsUsable: true });

const reject = (reason: SummaryDispatchRejection): SummaryDispatchVerdict => ({
    canSendToPartner: false,
    identifierIsUsable: false,
    reason,
});

/**
 * Decides whether the session summary may be forwarded to the partner's tool (ACSS / OPUS notes).
 *
 * This validates the value that is *actually dispatched* as `accountNo`, which differs by partner:
 * with `useMdnForAccountNo` (att) it is the customer MDN, otherwise it is the account number. The
 * previous check only ever looked at the account number, so on att it validated a field that was not
 * being sent, against lengths (9 or 12) that a 10-digit MDN can never satisfy, and its result never
 * gated the dispatch at all.
 *
 * Every rule here rejects a value that *cannot* be a subscriber number, so nothing legitimate is
 * suppressed. Deliberately absent is any rule requiring a positive signal that the number is good:
 * the IVR's `SUBSCRIBER_VALIDATED` / `MDN_Captured` attributes have high precision but only about 65%
 * recall on good traffic, so requiring them measured at 143,396 suppressed legitimate summaries over
 * 71 days, roughly 6.75x the entire junk population this gate exists to stop. Those flags are only
 * ever safe as an override that rescues an otherwise suspect value, never as a requirement.
 */
export const evaluateSummaryDispatch = ({
    accountNumber,
    allowedAccountNumberLengths,
    blocklistedNumbers,
    blocklistedPrefixes,
    blocklistedSubscriberNumbers,
    subscriberNumber,
    useMdnForAccountNo = false,
}: {
    accountNumber?: string;
    allowedAccountNumberLengths?: number[];
    blocklistedNumbers?: string[];
    blocklistedPrefixes?: string[];
    /**
     * Well-formed numbers that can never belong to a subscriber, from partner config. Today these are
     * AT&T's own contact-center trunk ANIs, which arrive as the caller ID on National Assistance
     * Center warm transfers. No format or length rule can reject them, so a denylist is the only
     * thing that catches them.
     */
    blocklistedSubscriberNumbers?: string[];
    /** The MDN as it will be dispatched, i.e. after `removeCountryCode`. */
    subscriberNumber: string;
    useMdnForAccountNo?: boolean;
}): SummaryDispatchVerdict => {
    if (!useMdnForAccountNo) {
        const validAccountNumber =
            accountNumber !== undefined && (allowedAccountNumberLengths?.includes(accountNumber.length) ?? false);
        // The configured-length check has never gated the send for these partners, and it was only
        // ever a stats label. Keep it that way: this fix targets the MDN path, and newly suppressing
        // another partner's notes on an untested length rule would be a regression, not a fix.
        return {
            canSendToPartner: true,
            identifierIsUsable: validAccountNumber,
            ...(validAccountNumber ? {} : { reason: "account-number-length" as const }),
        };
    }

    const value = subscriberNumber?.trim() ?? "";
    if (!value) return reject("empty");
    if (SUBSCRIBER_NUMBER_SENTINELS.has(value.toLowerCase())) return reject("sentinel");
    if (value.replace(/\D/g, "").length !== SUBSCRIBER_NUMBER_DIGITS) return reject("not-ten-digits");
    // Checked separately from `blocklistedNumbers` so the log says which list matched, and because
    // that list also drives what the customer panel displays while this one does not.
    if (isBlocklistedNumber(value, blocklistedSubscriberNumbers)) return reject("non-subscriber-number");
    if (isBlocklistedNumber(value, blocklistedNumbers, blocklistedPrefixes)) return reject("blocklisted");

    return ALLOWED;
};
